<template>
  <!--
    Reusable highlighted-tag row. On mobile it is a full-bleed horizontal carousel
    (breaks out of the container gutter and scrolls); from lg the bleed is removed
    and the tags are centred. Used by /actueel (filter mode) and /groei (link mode).
  -->
  <ul
    v-if="entries.length"
    class="flex gap-2 overflow-x-auto scrollbar-hide w-screen ml-[calc(50%_-_50vw)] px-4 lg:mt-6 lg:w-full lg:ml-0 lg:px-0 lg:justify-center"
    data-component="HighlightedTags">
    <li v-for="(entry, index) in entries" :key="`${entryValue(entry)}-${index}`" class="shrink-0">
      <!-- Filter mode: a button that toggles the active tag. -->
      <Button
        v-if="mode === 'filter'"
        :variant="isActive(entry) ? 'primary' : 'tag-white'"
        :aria-pressed="isActive(entry)"
        size="md"
        as="button"
        type="button"
        class="tag-pill gap-2"
        :class="{ 'is-active': isActive(entry) }"
        @click="emit('toggle', entryValue(entry))">
        <img
          v-if="entry.icon?.src"
          :src="entry.icon.src"
          :alt="entry.icon.altText ?? ''"
          aria-hidden="true"
          class="w-4 h-4 object-contain" >
        {{ entry.title }}
      </Button>

      <!--
        Link mode: navigates via the entry link, or renders a plain (non-interactive)
        pill when there is no link. The `value` field is not used for navigation.
      -->
      <Button
        v-else
        :as="entry.link ? 'NuxtLink' : 'span'"
        :to="entry.link?.url"
        :external="entry.link?.openInNewTab"
        variant="tag-white"
        size="md"
        class="tag-pill gap-2">
        <img
          v-if="entry.icon?.src"
          :src="entry.icon.src"
          :alt="entry.icon.altText ?? ''"
          aria-hidden="true"
          class="w-4 h-4 object-contain" >
        {{ entry.title }}
      </Button>
    </li>
  </ul>
</template>

<script setup lang="ts">
import type { ImageApiModel, LinkApiModel } from '@gro-up/api/corporate'
import { Button } from '@gro-up/ui/primitives'

export type HighlightedTagEntry = {
  title: string
  icon?: ImageApiModel | null
  link?: LinkApiModel | null
  value?: string | null
}

const props = withDefaults(
  defineProps<{
    entries: HighlightedTagEntry[]
    /** 'filter' = toggle buttons with an active state; 'link' = navigation pills. */
    mode?: 'filter' | 'link'
    /** Active tag value, used to highlight the selected pill in filter mode. */
    activeValue?: string | null
  }>(),
  {
    mode: 'link',
    activeValue: null,
  },
)

const emit = defineEmits<{
  /** Filter mode only: the clicked tag's value. */
  toggle: [value: string]
}>()

// Fall back to the title when no explicit value is provided.
function entryValue(entry: HighlightedTagEntry): string {
  return entry.value ?? entry.title
}

function isActive(entry: HighlightedTagEntry): boolean {
  return props.mode === 'filter' && entryValue(entry) === props.activeValue
}
</script>

<style scoped>
/* "Hack", omdat we geen invloed hebben op wat voor icoon er nu geupload wordt:
   bij hover/actief wordt de achtergrond donker, dus de icoon inverteren. */
.tag-pill.is-active img,
.tag-pill:hover img {
  transition: all 0.2s ease;
  filter: grayscale(1) invert(1);
}
</style>
